import websocketUrl from "@/config/socketUrl"
import { notificationTypes } from "@/store/modules/notification"

class WebSocketService {
  constructor(store) {
    this.store = store
    this.ws = null
    this.wsTimer = null
    this.heartbeatTimer = null
    this.reconnectTimer = null
    this.reconnectCount = 0
    this.maxReconnectAttempts = 5
    this.reconnectDelay = 3000
    this.isManualClose = false
  }

  connect(token) {
    if (!token) {
      console.error('No token provided for WebSocket connection')
      return
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket is already connected')
      return
    }

    this.isManualClose = false
    this.createWebSocket(token)
  }

  createWebSocket(token) {
    try {
      this.ws = new WebSocket(websocketUrl + token)
      this.initEventHandlers()
    } catch (error) {
      console.error('Failed to create WebSocket:', error)
      this.handleReconnect()
    }
  }

  initEventHandlers() {
    this.ws.onerror = () => {
      console.error('WebSocket connection error')
      this.store.commit('notification/SET_CONNECTED', false)
    }

    this.ws.onopen = () => {
      console.log('WebSocket connected successfully')
      this.reconnectCount = 0
      this.store.commit('notification/SET_CONNECTED', true)
      this.startHeartbeat()
      this.store.dispatch('notification/init')
    }

    this.ws.onmessage = (event) => {
      if (event.data === 'pong') {
        return
      }
      this.handleMessage(event.data)
    }

    this.ws.onclose = (event) => {
      console.log('WebSocket closed:', event.code, event.reason)
      this.store.commit('notification/SET_CONNECTED', false)
      this.stopHeartbeat()
      
      if (!this.isManualClose) {
        this.handleReconnect()
      }
    }
  }

  handleMessage(data) {
    try {
      const msgMap = JSON.parse(data)
      const notification = {
        id: msgMap.id || Date.now().toString(),
        title: msgMap.title,
        text: msgMap.text || msgMap.content || '',
        type: this.normalizeType(msgMap.type),
        fromName: msgMap.fromName || '系统',
        priority: msgMap.priority || this.getDefaultPriority(msgMap.type),
        createTime: msgMap.createTime || new Date().toISOString(),
        toPath: msgMap.toPath,
        backPath: msgMap.backPath,
        detail: msgMap.detail
      }

      this.store.dispatch('notification/addNotification', notification)
      
      if (msgMap.num !== undefined) {
        this.store.commit('notification/SET_UNREAD_COUNT', {
          total: msgMap.num
        })
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error)
    }
  }

  normalizeType(type) {
    const typeMap = {
      '0': notificationTypes.MESSAGE,
      '1': notificationTypes.NOTICE,
      '2': notificationTypes.ALERT,
      'message': notificationTypes.MESSAGE,
      'notice': notificationTypes.NOTICE,
      'alert': notificationTypes.ALERT
    }
    return typeMap[type] || notificationTypes.NOTICE
  }

  getDefaultPriority(type) {
    const priorityMap = {
      [notificationTypes.MESSAGE]: 'low',
      [notificationTypes.NOTICE]: 'medium',
      [notificationTypes.ALERT]: 'urgent'
    }
    return priorityMap[type] || 'medium'
  }

  startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send('ping')
        } catch (error) {
          console.error('Failed to send heartbeat:', error)
        }
      }
    }, 60000)
  }

  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  handleReconnect() {
    if (this.reconnectCount >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached')
      return
    }

    this.stopReconnectTimer()
    
    this.reconnectTimer = setTimeout(() => {
      this.reconnectCount++
      console.log(`Attempting to reconnect (${this.reconnectCount}/${this.maxReconnectAttempts})...`)
      
      const token = this.store.state.user.UserToken
      if (token) {
        this.createWebSocket(token)
      }
    }, this.reconnectDelay)
  }

  stopReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  disconnect() {
    this.isManualClose = true
    this.stopHeartbeat()
    this.stopReconnectTimer()
    
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
    
    this.store.commit('notification/SET_CONNECTED', false)
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN
  }
}

let instance = null

export const getWebSocketService = (store) => {
  if (!instance) {
    instance = new WebSocketService(store)
  }
  return instance
}

export default WebSocketService
